import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Gift, Share2, Copy, X, Hourglass } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/lib/auth";
import { api, withRef } from "@/lib/appresse";
import { useChallenge, fmtCountdown } from "@/lib/challenge";

// First-page ambassador challenge: a blinking countdown strip + a once-a-day
// "how to share" banner shown only to ambassadors who have never shared.
export function AmbassadorChallengeHome() {
  const { user } = useAuth();
  const ch = useChallenge();
  const navigate = useNavigate();
  const [howto, setHowto] = useState(false);
  const [link, setLink] = useState("");

  // Réservé aux ambassadeurs désignés + admins/super admins (jamais aux clients).
  const ambAllowed = !!user && (!!user.ambassador || user.role === "admin");

  const dayKey = user ? `appresse_share_howto_${user.id}_${new Date().toISOString().slice(0, 10)}` : null;

  useEffect(() => {
    if (!user || ch.loading || !ch.active || ch.hasShared || !dayKey) return;
    let seen = false;
    try { seen = localStorage.getItem(dayKey) === "1"; } catch { /* ignore */ }
    if (!seen) {
      const t = setTimeout(() => setHowto(true), 1200);
      return () => clearTimeout(t);
    }
  }, [user, ch.loading, ch.active, ch.hasShared, dayKey]);

  useEffect(() => {
    if (howto && !link) api.myAmbassador().then((d) => setLink(d.share_link || "")).catch(() => {});
  }, [howto, link]);

  const closeHowto = useCallback(() => {
    setHowto(false);
    if (dayKey) { try { localStorage.setItem(dayKey, "1"); } catch { /* ignore */ } }
  }, [dayKey]);

  const share = useCallback(async () => {
    const url = withRef(link || window.location.origin);
    const text = "Je t'invite sur APP PRESSE : tous les journaux et magazines sur ton téléphone. Profite des invitations gratuites avant la fin !";
    try {
      if (navigator.share) { await navigator.share({ title: "APP PRESSE", text, url }); api.trackSiteShare?.("native").catch(() => {}); }
      else { await navigator.clipboard.writeText(`${text} ${url}`); toast.success("Lien copié — collez-le à vos amis !"); }
    } catch { /* user cancelled */ }
  }, [link]);

  const copyLink = useCallback(async () => {
    try { await navigator.clipboard.writeText(withRef(link || window.location.origin)); toast.success("Lien de parrainage copié !"); }
    catch { toast.error("Impossible de copier"); }
  }, [link]);

  if (!user || !ambAllowed || ch.loading || !ch.active) return null;

  return (
    <>
      <div
        data-testid="challenge-countdown-strip"
        className="mt-4 rounded-2xl p-[1.5px] bg-gradient-to-r from-primary via-amber-400 to-primary shadow-[0_8px_22px_-8px_rgba(0,0,0,0.5)]"
      >
        <button
          onClick={() => navigate("/ambassadeur")}
          className="w-full flex items-center gap-3 rounded-2xl bg-card px-4 py-3 text-left hover:bg-secondary/40 transition-colors"
          data-testid="challenge-countdown-cta"
        >
          <span className="shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-primary animate-pulse" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-semibold text-muted-foreground flex items-center gap-1">
              <Hourglass className="w-3 h-3 text-primary" /> Challenge ambassadeur — invitations gratuites
            </span>
            <span className="block font-serif font-black text-lg tabular-nums leading-tight" data-testid="challenge-countdown-value">
              {fmtCountdown(ch.parts)}
            </span>
          </span>
          <span className="shrink-0 hidden sm:inline-flex items-center gap-1 rounded-full bg-primary text-primary-foreground text-xs font-semibold px-3 py-1.5">
            <Share2 className="w-3.5 h-3.5" /> Partager
          </span>
        </button>
      </div>

      <Dialog open={howto} onOpenChange={(o) => { if (!o) closeHowto(); }}>
        <DialogContent className="sm:max-w-md rounded-2xl" data-testid="share-howto-dialog">
          <DialogHeader className="items-center text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
              <Gift className="w-7 h-7 text-primary" />
            </div>
            <DialogTitle className="font-serif font-black text-xl">
              ⏳ Plus que {fmtCountdown(ch.parts)} !
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Les invitations gratuites seront bientôt terminées. Vous n'avez pas encore partagé —
              voici comment inviter vos amis et votre famille en 30 secondes :
            </DialogDescription>
          </DialogHeader>

          <ol className="space-y-2 text-sm">
            {[
              "Copiez votre lien de parrainage personnel ci-dessous.",
              "Envoyez-le par WhatsApp, SMS ou e-mail à vos proches.",
              "Chaque inscription via votre lien vous fait gagner des points et des cadeaux !",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="shrink-0 w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>

          {link && (
            <button onClick={copyLink} data-testid="share-howto-copy"
              className="w-full flex items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm hover:bg-secondary transition-colors">
              <Copy className="w-4 h-4 text-primary shrink-0" />
              <span className="truncate text-left flex-1">{link}</span>
              <span className="text-xs font-semibold text-primary shrink-0">Copier</span>
            </button>
          )}

          <div className="flex flex-col gap-2 pt-1">
            <Button className="w-full gap-2" onClick={share} data-testid="share-howto-share">
              <Share2 className="w-4 h-4" /> Partager maintenant
            </Button>
            <Button variant="ghost" className="w-full text-muted-foreground" onClick={closeHowto} data-testid="share-howto-later">
              Plus tard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
